import React, { useCallback, useMemo } from 'react';
import { CompanySelect } from '@block/hr-fe';
import type { CompanySelectProps } from '@block/hr-fe/dist/company-select/interface';

// 封装组件的 Props 接口，将 value 和 onChange 改为字符串类型
export interface CompanyProps extends Omit<CompanySelectProps, 'value' | 'onChange' | 'onSelect'> {
  /** 公司名称字符串，id 和 name 相同 */
  value?: string;
  /** 值变更回调，返回公司名称字符串 */
  onChange?: (value: string | null) => void;
  /** 选择回调，返回公司名称字符串 */
  onSelect?: (value: string | null) => void;
}

/**
 * 封装的公司选择组件
 * 基于 @block/hr-fe 的 CompanySelect，简化输入输出为字符串格式
 *
 * @param props - 组件属性
 * @returns React 组件
 */
export const Company: React.FC<CompanyProps> = ({ value, onChange, onSelect, ...restProps }) => {
  // 将字符串值转换为原组件需要的对象格式
  const companyValue = useMemo(() => {
    if (!value) return undefined;

    return {
      value: {
        id: value,
        name: value,
      },
      disabled: false,
      selected: false,
      label: [`未查询到该公司，点击添加: `, value],
    };
  }, [value]);

  // 处理原组件的 onChange 回调
  const handleChange = useCallback(
    (selectedValue: any) => {
      if (!selectedValue) {
        onChange?.(null);
        return;
      }

      // 从原组件返回的复杂对象中提取公司名称
      const companyName = selectedValue?.value?.name || null;
      onChange?.(companyName);
    },
    [onChange],
  );

  // 处理原组件的 onSelect 回调
  const handleSelect = useCallback(
    (selectedValue: any) => {
      if (!selectedValue) {
        onSelect?.(null);
        return;
      }

      // 从原组件返回的复杂对象中提取公司名称
      const companyName = selectedValue?.value?.name || null;
      onSelect?.(companyName);
    },
    [onSelect],
  );

  return (
    <CompanySelect
      {...restProps}
      value={companyValue}
      onChange={handleChange}
      onSelect={handleSelect}
      fetchAPI={{
        url: '/api/candidate/info/common/filter',
      }}
    />
  );
};
